import { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/cms/ui/alert-dialog';
import { IconButton } from '@/components/ui/icon-button';
import { Input } from '@/components/cms/ui/input';
import LureoButton from '@/common/LureoButton';
import { Card } from '@/components/ui/card';
import { v1 } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { projectToLegacy } from '@/infrastructure/api/v1/mappers/projects';
import { useAuthUser } from '@/hooks/useAuthUser';
import { ProjectInstance, SupabaseProjectInstance } from '@/types/projectTypes';
import { convertToProjectInstance } from '@/utils/dataConverters';
import { ArrowLeftIcon, CheckIcon } from '@phosphor-icons/react';
import { CONSUMED_TEMPLATES_CATEGORY, PUBLISHED_TEMPLATES_CATEGORY } from '@/modules/templateScenes/templateProjects';

interface TemplateExitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: 'exit' | 'publish';
  canDiscard?: boolean;
  onDiscard?: () => void;
  onSaveChanges?: () => void;
  onSaveAsNewProject: (newProjectName?: string) => void;
  onAddToExistingProject: (projectId: string) => void;
}

async function fetchUserProjects(userId: string): Promise<ProjectInstance[]> {
  try {
    // Both template containers hidden; projects without a category are kept. Newest update first.
    const response = await listAll(page =>
      v1.GET('/api/v1/projects', {
        params: {
          query: {
            userUniqueId: userId,
            excludeCategory: [CONSUMED_TEMPLATES_CATEGORY, PUBLISHED_TEMPLATES_CATEGORY],
            sort: '-updatedAt',
            ...page,
          },
        },
      }),
    );

    if (response.error) {
      console.error('Error fetching projects:', response.error);
      return [];
    }
    return response.data.map(dto => convertToProjectInstance(projectToLegacy(dto) as unknown as SupabaseProjectInstance));
  } catch (error) {
    console.error('Error fetching projects:', error);
    return [];
  }
}

export function TemplateExitDialog({
  open,
  onOpenChange,
  mode = 'exit',
  canDiscard = false,
  onDiscard,
  onSaveChanges,
  onSaveAsNewProject,
  onAddToExistingProject
}: TemplateExitDialogProps) {
  const { user } = useAuthUser();
  const userId = user?.id;
  const [step, setStep] = useState<'save-or-discard' | 'save-options'>('save-or-discard');
  const [projects, setProjects] = useState<ProjectInstance[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [showProjectList, setShowProjectList] = useState(false);
  const [showSaveToNewProject, setShowSaveToNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');

  // Fetch user projects when dialog opens and we're on the "add to existing" step
  const fetchKey = open && step === 'save-options' && userId ? userId : null;
  const [prevFetchKey, setPrevFetchKey] = useState(fetchKey);
  if (prevFetchKey !== fetchKey) {
    setPrevFetchKey(fetchKey);
    if (fetchKey) setIsLoadingProjects(true);
  }
  useEffect(() => {
    if (!fetchKey) return;
    (async () => {
      const list = await fetchUserProjects(fetchKey);
      setProjects(list);
      setIsLoadingProjects(false);
    })();
  }, [fetchKey]);

  // Reset state when dialog closes
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (open) {
      setStep(mode === 'publish' ? 'save-options' : 'save-or-discard');
    } else {
      setStep('save-or-discard');
      setShowProjectList(false);
      setShowSaveToNewProject(false);
      setNewProjectName('');
    }
  }

  const handleDiscard = () => {
    onDiscard?.();
    onOpenChange(false);
  };

  const handleSave = () => {
    setStep('save-options');
  };

  const handleBack = () => {
    setStep('save-or-discard');
    setShowProjectList(false);
    setShowSaveToNewProject(false);
    setNewProjectName('');
  };

  const handleSaveChanges = () => {
    onSaveChanges?.();
    onOpenChange(false);
  };

  const handleShowSaveToNewProject = () => {
    setShowProjectList(false);
    setShowSaveToNewProject(true);
  };

  const handleConfirmSaveAsNewProject = () => {
    onSaveAsNewProject(newProjectName?.trim() || undefined);
    onOpenChange(false);
  };

  const handleShowProjectList = () => {
    setShowSaveToNewProject(false);
    setNewProjectName('');
    setShowProjectList(true);
    if (projects.length === 0 && userId) {
      setIsLoadingProjects(true);
      fetchUserProjects(userId).then((list) => {
        setProjects(list);
        setIsLoadingProjects(false);
      });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="cursor-default select-none">
        {step === 'save-or-discard' ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Keep this template scene?</AlertDialogTitle>
              <AlertDialogDescription>
                It was added to My Templates when you opened it.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter>
              {/* Stays in the scene, as the dialog's close button did */}
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              {canDiscard && (
                <LureoButton
                  variant="outline-3"
                  suppressTooltip
                  onClick={handleDiscard}
                  className="h-9 px-5 rounded-lureo-radius-button-pill"
                >
                  Remove
                </LureoButton>
              )}
              <LureoButton
                variant="secondary"
                suppressTooltip
                onClick={handleSave}
                className="h-9 px-4 rounded-lureo-radius-button-pill"
              >
                Keep
              </LureoButton>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              {mode === 'publish' ? (
                <AlertDialogTitle>Save to a project to publish</AlertDialogTitle>
              ) : (
                <div className="flex items-center gap-3">
                  <IconButton label="Go back" place="dialog" onClick={handleBack}>
                    <ArrowLeftIcon className="h-4 w-4" />
                  </IconButton>
                  <AlertDialogTitle>Where to keep it</AlertDialogTitle>
                </div>
              )}
              <AlertDialogDescription>
                {mode === 'publish'
                  ? "Scenes in My Templates can't be published. Save this scene into one of your projects, then publish it from there."
                  : 'Choose where this template scene lives'}
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogBody>
              {mode === 'exit' && (
                <LureoButton
                  variant="outline-3"
                  suppressTooltip
                  justify="start"
                  onClick={handleSaveChanges}
                  className="h-12 w-full rounded-lureo-radius-button-pill px-6"
                >
                  Keep in My Templates
                </LureoButton>
              )}

              {!showSaveToNewProject ? (
                <LureoButton
                  variant="outline-3"
                  suppressTooltip
                  justify="start"
                  onClick={handleShowSaveToNewProject}
                  className="h-12 w-full rounded-lureo-radius-button-pill px-6"
                >
                  Save to New Project
                </LureoButton>
              ) : (
                <div className="space-y-3 pt-2">
                  <div className="text-lureo-14 font-medium text-lureo-ink px-2 leading-5">
                    Name your new project (optional):
                  </div>
                  <Input
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    placeholder="Enter project name..."
                    autoFocus
                  />
                  <LureoButton
                    variant="secondary"
                    suppressTooltip
                    onClick={handleConfirmSaveAsNewProject}
                    className="h-12 w-full rounded-lureo-radius-button-pill px-6"
                  >
                    <CheckIcon className="h-4 w-4" />
                    Create Project
                  </LureoButton>
                </div>
              )}

              {!showProjectList ? (
                <LureoButton
                  variant="outline-3"
                  suppressTooltip
                  justify="start"
                  onClick={handleShowProjectList}
                  className="h-12 w-full rounded-lureo-radius-button-pill px-6"
                >
                  Add Scene to Existing Project
                </LureoButton>
              ) : (
                <div className="space-y-3 pt-2">
                  <div className="text-lureo-14 font-medium text-lureo-ink px-2 leading-5">
                    Select a project:
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {isLoadingProjects ? (
                      <div className="text-lureo-13 text-lureo-ink-subtle py-4 px-2 text-center">
                        Loading projects...
                      </div>
                    ) : projects.length > 0 ? (
                      projects.map((project) => (
                        <Card
                          key={project.id}
                          padding="none"
                          className="group flex items-center justify-between gap-3 px-4 py-3"
                        >
                          <h3 className="truncate text-lureo-16 font-semibold leading-tight text-lureo-ink flex-1">
                            {project.projectName || 'Untitled Project'}
                          </h3>
                          <LureoButton
                            variant="outline-2"
                            suppressTooltip
                            className="h-7 shrink-0 gap-1.5 rounded-lureo-radius-button-pill bg-lureo-surface px-3 text-lureo-12 font-medium"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddToExistingProject(project.id);
                              onOpenChange(false);
                            }}
                          >
                            <CheckIcon className="h-3.5 w-3.5" />
                            Save
                          </LureoButton>
                        </Card>
                      ))
                    ) : (
                      <div className="text-lureo-13 text-lureo-ink-subtle py-4 px-2 text-center">
                        No projects available
                      </div>
                    )}
                  </div>
                </div>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              {/* Stays in the scene, as the dialog's close button did */}
              <AlertDialogCancel>Cancel</AlertDialogCancel>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}

